// Packages
import React, { useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Api from "../../core/Api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    padding: 0,
  },
};

const validationSchema = Yup.object().shape({
  gsmCode: Yup.string()
    .required("Bu alan boş bırakılamaz.")
    .min(4, "Kod 4 karakter olmalıdır")
    .max(4, "Kod 4 karakter olmalıdır")
    .nullable(false),
});

const showToast = (message, type = "error") => {
  toast[type](message, {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: undefined,
    theme: "light",
  });
};

const SmsValidator = ({
  isOpen,
  onClose,
  onVerify,
  formikData,
  isUser,
  isEvet,
}) => {
  const [isProgress, setIsProgress] = useState(false);
  const navigate = useNavigate();

  const handleEvetValidation = async (data) => {
    try {
      const resp = await Api.smsCodeEvetLoginAsync({
        firmType: formikData.firmType,
        idNo: formikData.idNo,
        gsm: formikData.gsm,
        idBranch: formikData.idBranch,
        ...data,
      });

      if (resp.data.data.token) {
        onVerify(resp.data.data);
        navigate("/request-list");
      }
    } catch (err) {
      showToast("Hatalı doğrulama kodu girdiniz.");
    }
  };

  const handleUserValidation = async (data) => {
    try {
      if (formikData.isRegister) {
        const response = await Api.saveAccountAsync({
          ...formikData,
          ...data,
        });
        if (response.data.data.isSucceded) {
          navigate("/user-login");
        }
      } else {
        const resp = await Api.authCustomerAsync({
          identityNr: formikData.identityNr,
          gsm: formikData.gsm,
          idBranch: formikData.idBranch,
          ...data,
        });

        if (resp.data.data.token) {
          onVerify(resp.data.data);
          navigate("/user-home");
        }
      }
    } catch (err) {
      showToast("Hatalı doğrulama kodu girdiniz.");
    }
  };

  const handleClinicValidation = async (data) => {
    try {
      if (formikData.isRegister) {
        const response = await Api.saveAccountAsync({
          ...formikData,
          ...data,
        });
        if (response.data.data.isSucceded) {
          showToast("Başarıyla kayıt oldunuz.", "success");
          navigate("/clinic-login");
        }
      } else {
        const resp = await Api.smsCodeValidationAsync({
          firmType: formikData.firmType,
          idNo: formikData.idNo,
          gsm: formikData.gsm,
          idBranch: formikData.idBranch,
          SecField1: "",
          SecField2: "",
          ...data,
        });

        if (resp.data.data.token) {
          onVerify(resp.data.data);
          showToast("Başarıyla giriş yaptınız.", "success");
          navigate("/request-list");
        }
      }
    } catch (err) {
      showToast("Hatalı doğrulama kodu girdiniz.");
    }
  };

  const formik = useFormik({
    initialValues: {
      gsmCode: "",
    },
    validationSchema,
    onSubmit: async (data) => {
      setIsProgress(true);
      try {
        if (isEvet) {
          await handleEvetValidation(data);
        } else if (isUser) {
          await handleUserValidation(data);
        } else {
          await handleClinicValidation(data);
        }
      } finally {
        setIsProgress(false);
      }
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      contentLabel="Doğrulama Kodu"
    >
      <div>
        <div className="flex justify-between border-b p-3">
          <div className="font-bold">Sms Doğrulama</div>
          <button
            className="bg-red-600 text-white px-4 rounded-md"
            onClick={onClose}
          >
            X
          </button>
        </div>

        <div className="p-8">
          <form onSubmit={formik.handleSubmit}>
            <input
              type="text"
              value={formik.values.gsmCode}
              onChange={formik.handleChange}
              placeholder="Doğrulama Kodu"
              maxLength="4"
              id="gsmCode"
              className="rounded-md white text-base p-2 pl-4 mb-4 w-full focus:border-yellow-500 duration-300 border-2 border-gray-300"
            />
            <span className="text-xs text-red-600 font-semibold flex">
              {formik.touched.gsmCode && formik.errors.gsmCode}
            </span>
            <button
              disabled={isProgress}
              type="submit"
              className="w-full font-semibold bg-red-600 text-white rounded-md py-2 mt-2 hover:bg-red-700 duration-300"
            >
              {isProgress ? "Gönderiliyor..." : "Gönder"}
            </button>
          </form>
        </div>
      </div>
    </Modal>
  );
};

SmsValidator.defaultProps = {
  isOpen: false,
};

SmsValidator.propTypes = {
  firmType: PropTypes.number,
  idNo: PropTypes.string,
  gsmCode: PropTypes.string,
  gsm: PropTypes.string,
  isOpen: PropTypes.bool,
  isRegister: PropTypes.bool,
  onClose: PropTypes.func,
  onVerify: PropTypes.func,
};

export default SmsValidator;
